import { useState, useEffect, useRef, useMemo } from 'react';
import { paletteSurfaces } from '../../lib/surfaces';

interface CmdKProps {
  open: boolean;
  onClose: () => void;
  onNav: (id: string) => void;
}

// The command palette. Its rows come from lib/surfaces (the same registry behind the desktop pills
// and the mobile tabs), so it can no longer list a stale subset of the app — it previously carried
// 6 of 13 surfaces and was the only nav control that survives on mobile.
//
// The footer advertises "↑↓ NAVIGATE ⏎ OPEN"; before this the only key handler was Escape and the
// first row was hardcoded `.active`, so the app's global finder was mouse-only while claiming
// otherwise. Selection is real state now.
export function CmdK({ open, onClose, onNav }: CmdKProps) {
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const surfaces = useMemo(() => paletteSurfaces(), []);
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return surfaces;
    return surfaces.filter(
      (s) => s.label.toLowerCase().includes(query) || s.blurb.toLowerCase().includes(query)
    );
  }, [q, surfaces]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Reset the cursor whenever the result set changes, so Enter can never fire the row that
  // happened to sit at the old index.
  useEffect(() => { setSelected(0); }, [q]);
  useEffect(() => { if (open) { setQ(''); setSelected(0); } }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelected((i) => (filtered.length === 0 ? 0 : (i + 1) % filtered.length));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected((i) => (filtered.length === 0 ? 0 : (i - 1 + filtered.length) % filtered.length));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const target = filtered[selected];
        if (target) { onNav(target.id); onClose(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, onNav, filtered, selected]);

  // Keep the cursor in view when arrowing past the visible window.
  useEffect(() => {
    const row = listRef.current?.querySelector<HTMLElement>('[data-cmdk-selected="true"]');
    row?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  if (!open) return null;

  return (
    <div className="cmdk-backdrop" onClick={onClose}>
      <div className="cmdk-modal" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <div className="cmdk-input-row">
          <span className="prefix">&#8984;K</span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)}
            placeholder="Search surfaces..."
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            aria-activedescendant={filtered[selected] ? `cmdk-row-${filtered[selected].id}` : undefined}
          />
          <kbd>ESC</kbd>
        </div>
        <div ref={listRef} id="cmdk-list" role="listbox" style={{ padding: '8px 0', maxHeight: 360, overflow: 'auto' }}>
          <div className="cmdk-group-label">SURFACES</div>
          {filtered.map((s, i) => (
            <div
              key={s.id}
              id={`cmdk-row-${s.id}`}
              role="option"
              aria-selected={i === selected}
              data-cmdk-selected={i === selected ? 'true' : undefined}
              className={'cmdk-item' + (i === selected ? ' active' : '')}
              onMouseEnter={() => setSelected(i)}
              onClick={() => { onNav(s.id); onClose(); }}
            >
              <span>{s.blurb ? `${s.label} - ${s.blurb}` : s.label}</span>
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '12px 16px', color: 'var(--text3)', fontSize: 13 }}>No results</div>
          )}
        </div>
        <div className="cmdk-footer">
          <span>&uarr;&darr; NAVIGATE</span>
          <span>&#9166; OPEN</span>
          <span style={{ marginLeft: 'auto' }}>POWERED BY YOUR VAULT</span>
        </div>
      </div>
    </div>
  );
}
